import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MAX_URL_LENGTH, NotAStoreDetailsSchema, type SessionSnapshot } from '@/contracts/onboarding-session';
import { HttpError } from '@/features/onboarding/services/http-client';

import { readWebsiteReply } from '../quiz/website';
import { putSession } from '../services/onboarding-session.service';
import type { TranscriptData } from '../state/buildTranscript';
import { onboardingKeys } from './queryKeys';
import { isReattachRefused } from './useSessionSnapshot';

export type RejectedUrl = NonNullable<TranscriptData['rejectedUrl']>;

function errorCode(error: unknown): string | undefined {
  return error instanceof HttpError ? error.envelope?.error?.code : undefined;
}

/** 429: too many sessions started, or too many live ones, for this operator. */
export const isRateLimited = (error: unknown): boolean => errorCode(error) === 'rate_limited';
/** 409: the session id already exists for a different site. */
export const isUrlMismatch = (error: unknown): boolean => errorCode(error) === 'session_url_mismatch';

/** How the chat answers a refused address, or null when the failure is not a refusal. */
export function rejectionOf(error: unknown, raw: string): RejectedUrl | null {
  const code = errorCode(error);
  if (code === 'rate_limited') return { raw, reason: 'rate_limited' };
  if (code !== 'not_a_store' && code !== 'invalid_url') return null;
  const details = NotAStoreDetailsSchema.safeParse((error as HttpError).envelope?.error?.details);
  return { raw, reason: code, ...(details.success ? { presence: details.data } : {}) };
}

export interface UseUrlAttachOptions {
  sessionId: string;
  /** The site the browser session holds; null until one was accepted. */
  url: string | null;
  submitUrl: (url: string) => void;
  rejectUrl: () => void;
  /** Use a new session id for the same site and answers. */
  rotateSessionId: () => void;
}

export interface UrlAttachApi {
  /** Validates, remembers and PUTs the site. Stable while the session id and callbacks are. */
  handleUrl: (raw: string) => void;
  /** The address the chat refused, in memory only. */
  rejected: RejectedUrl | null;
  /** The PUT is in flight (the composer waits). */
  pending: boolean;
  /**
   * True once the snapshot poll may start: the PUT settled (any way), or the session was resumed from storage.
   * Until then the PUT owns session creation, so the poll cannot race it into a second PUT.
   */
  attached: boolean;
}

/**
 * The website step: PUT creates or attaches the BFF session and starts its jobs. A refused address (not a
 * store, not a website, rate limited) goes back to asking; a 409 `session_url_mismatch` keeps the site and
 * answers under a fresh session id; any other failure leaves the url in place and the snapshot poll takes over
 * (it backs off, shows "Reconnecting…" and re-PUTs the same id and url once the BFF answers 404).
 */
export function useUrlAttach({
  sessionId,
  url,
  submitUrl,
  rejectUrl,
  rotateSessionId,
}: UseUrlAttachOptions): UrlAttachApi {
  const client = useQueryClient();
  const [rejected, setRejected] = useState<RejectedUrl | null>(null);
  // A resumed session (url already stored) has nothing to PUT yet: the poll GETs and re-PUTs only on 404.
  const [attached, setAttached] = useState(url !== null);

  const attach = useMutation({
    mutationFn: (vars: { sessionId: string; url: string }) => putSession(vars.sessionId, vars.url),
    onSuccess: (snapshot, vars) => {
      client.setQueryData<SessionSnapshot>(onboardingKeys.snapshot(vars.sessionId), snapshot);
    },
    onError: (error, vars) => {
      const refused = rejectionOf(error, vars.url);
      if (refused) {
        rejectUrl();
        setRejected(refused);
      } else if (isUrlMismatch(error)) {
        rotateSessionId();
      }
    },
    onSettled: () => setAttached(true),
  });
  const { mutate, isPending } = attach;

  const handleUrl = useCallback(
    (raw: string) => {
      const reply = readWebsiteReply(raw);
      if (reply.kind === 'invalid') return setRejected({ raw: raw.slice(0, MAX_URL_LENGTH), reason: 'invalid_url' });
      if (reply.kind === 'presence') return setRejected({ raw, reason: 'not_a_store', presence: undefined });
      setRejected(null);
      setAttached(false);
      submitUrl(raw);
      mutate({ sessionId, url: raw });
    },
    [mutate, sessionId, submitUrl],
  );

  return { handleUrl, rejected, pending: isPending, attached };
}

/** Most fresh session ids tried for one site before the chat stops rotating (the poll's backoff carries on). */
export const MAX_ROTATIONS = 3;

/**
 * The snapshot poll's own re-PUT (after a BFF restart) can find the session id taken by another site (409), or
 * owned by another operator (a 404 again: the BFF never says which). Answer both the same way as the first PUT,
 * with a new id for the same site and answers, a few times at most.
 */
export function useRotateOnUrlMismatch(snapshotError: unknown, rotateSessionId: () => void): void {
  const rotations = useRef(0);
  useEffect(() => {
    if (!(isUrlMismatch(snapshotError) || isReattachRefused(snapshotError)) || rotations.current >= MAX_ROTATIONS) return;
    rotations.current += 1;
    rotateSessionId();
  }, [snapshotError, rotateSessionId]);
}
